import {
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { CheckCircle, Cancel } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Session, SessionStatistics } from '@/types/session';
import { getCategoryColorByCode } from '@config/category-colors';
import { formatDate, formatDuration } from '@utils/date';


interface SessionSummaryProps {
  session: Session;
  statistics: SessionStatistics;
}

export function SessionSummary({ session, statistics }: SessionSummaryProps) {
  const { t } = useTranslation();

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom fontWeight={600}>
          {t('sessionSummary.title')}
        </Typography>
        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              {t('sessionSummary.facility')}
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {session.facilityName}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              {t('sessionSummary.ward')}
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {session.wardName}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              {t('sessionSummary.service')}
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {session.serviceName}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              {t('sessionSummary.observer')}
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {session.observerInitials}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              {t('sessionSummary.date')}
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {formatDate(session.sessionDate)}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              {t('sessionSummary.duration')}
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {session.durationMinutes
                ? formatDuration(session.durationMinutes)
                : t('sessionSummary.na')}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('sessionSummary.overallCompliance')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 3 }}>
          <Typography variant="h2" fontWeight={700} color="primary.main">
            {statistics.complianceRate.toFixed(1)}%
          </Typography>
          <Box>
            <Typography variant="body1" color="text.secondary">
              {t('sessionSummary.ofOpportunities', { performed: statistics.actionsPerformed, total: statistics.totalOpportunities })}
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main">
                {statistics.actionsPerformed}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <CheckCircle fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                {t('sessionSummary.performed')}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="error.main">
                {statistics.actionsMissed}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <Cancel fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                {t('sessionSummary.missedLabel')}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4">
                {statistics.glovesWornCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('sessionSummary.glovesWorn')}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle2" gutterBottom>
          {t('sessionSummary.methodBreakdown')}
        </Typography>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={4}>
            <Typography variant="h5">{statistics.byMethod.handrub}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t('sessionSummary.handrub')}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="h5">{statistics.byMethod.handwash}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t('sessionSummary.handwash')}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="h5">{statistics.byMethod.both}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t('sessionSummary.both')}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {statistics.byHCWCategory.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('sessionSummary.observationsByCategory')}
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('sessionSummary.categoryHeader')}</TableCell>
                  <TableCell align="right">{t('sessionSummary.hwsHeader')}</TableCell>
                  <TableCell align="right">{t('sessionSummary.oppsHeader')}</TableCell>
                  <TableCell align="right">{t('sessionSummary.complianceHeader')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {statistics.byHCWCategory.map((cat) => (
                  <TableRow key={cat.code} sx={{ borderLeft: `4px solid ${getCategoryColorByCode(cat.code)}` }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {cat.name}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{cat.healthworkerCount}</TableCell>
                    <TableCell align="right">{cat.opportunities}</TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color={
                          cat.compliance >= 80
                            ? 'success.main'
                            : cat.compliance >= 50
                              ? 'warning.main'
                              : 'error.main'
                        }
                      >
                        {cat.compliance.toFixed(1)}%
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {statistics.byIndication.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('sessionSummary.complianceByIndication')}
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('sessionSummary.momentHeader')}</TableCell>
                  <TableCell align="right">{t('sessionSummary.countHeader')}</TableCell>
                  <TableCell align="right">{t('sessionSummary.complianceHeader')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {statistics.byIndication.map((ind) => {
                  return (
                    <TableRow key={ind.indication}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {t(`indications.${ind.indication}.label`)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{ind.count}</TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color={
                            ind.compliance >= 80
                              ? 'success.main'
                              : ind.compliance >= 50
                                ? 'warning.main'
                                : 'error.main'
                          }
                        >
                          {ind.compliance.toFixed(1)}%
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}
